import { CalendarMonth } from '@mui/icons-material';
import {
  BadgeProps,
  Button,
  CircularProgress,
  Divider,
  styled,
  Typography,
} from '@mui/material';
import OverdueBanner from './OverdueBanner';
import PeriodCard from 'components/new/PeriodCard';
import StatusBadge from 'components/new/StatusBadge';
import Page from 'components/new/Page';
import { Column } from 'components/Layout';
import { differenceInDays } from 'date-fns';
import { CalendarDay } from 'further-ui/utils';
import { PeriodReviewStatus } from 'further-types/lp/accounting-period';
import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { minBy } from 'lodash';
import { useHistory } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';

const dotStatusMap: Record<PeriodReviewStatus, BadgeProps['color']> = {
  [PeriodReviewStatus.Overdue]: 'error',
  [PeriodReviewStatus.Pending]: 'warning',
  [PeriodReviewStatus.Approved]: 'success',
};

const DateRangeText = styled(Typography)({
  fontWeight: 500,
});

const getPendingStatusLabel = (
  reviewStatus: PeriodReviewStatus,
  dueDate: CalendarDay,
): string => {
  if (reviewStatus === PeriodReviewStatus.Overdue) {
    const daysOverdue = differenceInDays(
      new CalendarDay(new Date()).toUTCDate(),
      dueDate.toUTCDate(),
    );
    return `Overdue by ${daysOverdue} day${daysOverdue === 1 ? '' : 's'}`;
  }
  return `Due by ${dueDate.toReadable()}`;
};

const PeriodEndReview: React.FC = () => {
  const { vehicle } = useLayoutContext();
  const history = useHistory();
  const [canEdit] = useHasPermissions([RolePermission.LpEditAccountingPeriod]);
  const handleMutation = useHandleTrpcMutation<string>();

  const openPeriodReview = api.accountingPeriod.openPeriodReview.useMutation(
    handleMutation({
      onSuccess: (id) => {
        history.push(createLpPeriodEndReviewDetailRoute(id));
      },
    }),
  );

  const [periods] = api.accountingPeriod.listPeriods.useSuspenseQuery({
    vehicleId: vehicle?._id ?? '',
  });

  const pendingPeriods = periods.filter(
    (p) => p.reviewStatus !== PeriodReviewStatus.Approved,
  );
  const hasPendingPeriods = pendingPeriods.length > 0;
  const approvedPeriods = periods.filter(
    (p) => p.reviewStatus === PeriodReviewStatus.Approved,
  );
  const hasApprovedPeriods = approvedPeriods.length > 0;
  const oldestPendingPeriod = minBy(pendingPeriods, (period) =>
    period.periodEnd.toUTCDate().getTime(),
  );

  return (
    <Page
      tabTitle="Period-End Review"
      heading="Period-End Review"
      headingIcon={CalendarMonth}
      spacing="lg"
      withHeadingDivider
    >
      <Column spacing="lg">
        {oldestPendingPeriod?.reviewStatus === PeriodReviewStatus.Overdue && (
          <OverdueBanner dueDate={oldestPendingPeriod.dueDate} />
        )}
        <Typography variant="body2">Periods pending review</Typography>
        {hasPendingPeriods ? (
          pendingPeriods.map((period) => {
            const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;
            const statusLabel = getPendingStatusLabel(
              period.reviewStatus,
              period.dueDate,
            );
            return (
              <PeriodCard
                key={period.periodStart.toString()}
                icon={<StatusBadge color={dotStatusMap[period.reviewStatus]} />}
                primaryLabel={
                  <DateRangeText variant="h6">{dateRange}</DateRangeText>
                }
                secondaryLabel={
                  <Typography variant="body2">{statusLabel}</Typography>
                }
                actions={
                  canEdit ? (
                    <Button
                      variant="contained"
                      size="large"
                      disableElevation
                      disabled={
                        openPeriodReview.isPending ||
                        !vehicle?._id ||
                        period !== oldestPendingPeriod
                      }
                      startIcon={
                        openPeriodReview.isPending &&
                        period === oldestPendingPeriod ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : undefined
                      }
                      onClick={() =>
                        openPeriodReview.mutate({
                          vehicleId: vehicle?._id ?? '',
                          periodStart: period.periodStart,
                          periodEnd: period.periodEnd,
                          id: period.existingRecord?.id,
                        })
                      }
                    >
                      Review
                    </Button>
                  ) : undefined
                }
              />
            );
          })
        ) : (
          <Typography variant="body2">No periods pending close.</Typography>
        )}
        <Divider />
        <Typography variant="body2" color="text.secondary">
          Previously approved periods
        </Typography>
        {hasApprovedPeriods ? (
          approvedPeriods.map((period) => {
            const dateRange = `${period.periodStart.toReadable()} – ${period.periodEnd.toReadable()}`;
            const completedLabel = `Completed ${
              period.approvedAt?.toReadable() ?? 'N/A'
            }${period.approvedByLabel ? ` by ${period.approvedByLabel}` : ''}`;
            return (
              <PeriodCard
                key={period.periodStart.toString()}
                icon={<StatusBadge color="success" />}
                primaryLabel={
                  <DateRangeText variant="h6">{dateRange}</DateRangeText>
                }
                secondaryLabel={
                  <Typography variant="body2" color="text.secondary">
                    {completedLabel}
                  </Typography>
                }
                actions={
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() =>
                      history.push(
                        createLpPeriodEndReviewDetailRoute(
                          period.existingRecord?.id ?? '',
                        ),
                      )
                    }
                  >
                    View
                  </Button>
                }
              />
            );
          })
        ) : (
          <Typography variant="body2">
            No periods have been closed yet.
          </Typography>
        )}
      </Column>
    </Page>
  );
};

export default PeriodEndReview;
